import CloseIcon from "@mui/icons-material/Close";
import {
	Alert,
	Box,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	FormControl,
	FormHelperText,
	IconButton,
	InputLabel,
	MenuItem,
	Select,
	TextField,
} from "@mui/material";
import { DateTimePicker, LocalizationProvider } from "@mui/x-date-pickers-6";
import { AdapterDateFns } from "@mui/x-date-pickers-6/AdapterDateFns";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";

import { LaborStandard, laborStandardsApi } from "../../../../services/labor-standards-api";
import { productionPlanApi } from "../../../../services/production-plan-api";
import { Product } from "../../../../services/products-api";
import {
	parseAttributeValues,
	parseEnumOptions,
	WorkOrderAttributeDefinition, // eslint-disable-line sort-imports
	workOrderAttributesApi,
} from "../../../../services/work-order-attributes-api";
import { StoreType } from "../../../../utils/types";
import { GhostButton,planner,PrimaryButton } from "../../planner-ui"; // eslint-disable-line sort-imports
import { ProductMenuEmpty, ProductMenuSearch, useProductMenuSearch } from "../../planner-ui/product-menu-search";
import { operationRunsOn, standardHasStepOn } from "../../utils/standard-matching"; // eslint-disable-line sort-imports
import { toLocalIso } from "../board/plan-edits";
import { isNonProductionOrder, ManufacturingOrder } from "../utils/calculations"; // eslint-disable-line sort-imports

// One recipe for every control in the form, so the app theme's primary blue
// never shows up as a focus ring or floating label on a planning surface.
const formControlsSx = {
	"& .MuiOutlinedInput-root": {
		...planner.type.body,
		borderRadius: `${planner.radiusSm}px`,
		"& fieldset": { borderColor: planner.hairlineStrong },
		"&:hover fieldset": { borderColor: planner.navy },
		"&.Mui-focused fieldset": { borderColor: planner.accent },
	},
	"& .MuiInputLabel-root": { ...planner.type.meta, color: planner.inkMuted },
	"& .MuiInputLabel-root.Mui-focused": { color: planner.accent },
	"& .MuiFormHelperText-root": { ...planner.type.meta, color: planner.inkMuted },
	"& .Mui-error .MuiOutlinedInput-notchedOutline": { borderColor: planner.unstaffed },
	"& .MuiFormHelperText-root.Mui-error": { color: planner.unstaffed },
	"& .MuiInputLabel-root.Mui-error": { color: planner.unstaffed },
};

// Order type choices: Production is the default; the others render as
// non-production blocks (tinted, no product/labor standard) on the plan.
const ORDER_TYPE_OPTIONS = [
	{ value: "production", label: "Production" },
	{ value: "cleaning", label: "Cleaning" },
	{ value: "changeover", label: "Changeover" },
	{ value: "setup", label: "Setup" },
	{ value: "maintenance", label: "Maintenance" },
	{ value: "inspection", label: "Inspection" },
];

interface MOFormProps {
	open: boolean;
	onClose: () => void;
	onSave: () => void;
	editingMO: ManufacturingOrder | null;
	preselectedWorkcenter?: string | null;
	preselectedDate?: string | null;
	workcenters: string[];
	products: Product[];
}

// Validate a single attribute value against its definition (required/regex/type).
// Mirrors the server-side WorkOrderAttributeValidator rules.
const validateAttributeValue = (
	definition: WorkOrderAttributeDefinition,
	rawValue: string | undefined
): string | null => {
	const value = (rawValue || "").trim();
	if (!value) {
		return definition.isRequired ? `${definition.label} is required` : null;
	}
	if (definition.dataType === "number" && Number.isNaN(Number(value))) {
		return `${definition.label} must be a number`;
	}
	if (definition.dataType === "date" && Number.isNaN(new Date(value).getTime())) {
		return `${definition.label} must be a valid date`;
	}
	if (definition.dataType === "enum") {
		const options = parseEnumOptions(definition.enumOptions);
		if (options.length > 0 && !options.includes(value)) {
			return `${definition.label} must be one of: ${options.join(", ")}`;
		}
	}
	if (definition.validationRegex) {
		try {
			if (!new RegExp(definition.validationRegex).test(value)) {
				return `${definition.label} does not match the expected format`;
			}
		} catch {
			// Bad regex stored on the definition; do not block the value
		}
	}
	return null;
};

const MOForm: React.FC<MOFormProps> = ({ open, onClose, onSave, editingMO, preselectedWorkcenter, preselectedDate, workcenters, products }) => {
	const [laborStandards, setLaborStandards] = useState<LaborStandard[]>([]);
	const [loading, setLoading] = useState(false);
	const companyId = useSelector((state: StoreType) => state.user?.userData?.companyId);
	// The plant the planner is looking at; saved on the step so allocation and
	// the roster can scope by location.
	const selectedLocationId = useSelector(
		(state: StoreType) => state.fullTimeShifts?.selectedLocation ?? undefined
	);
	const [attributeDefinitions, setAttributeDefinitions] = useState<WorkOrderAttributeDefinition[]>([]);
	const [attributeValues, setAttributeValues] = useState<Record<string, string>>({});
	const [attributeErrors, setAttributeErrors] = useState<Record<string, string>>({});
	// Shown at the top of the form: why the last save did not go through.
	const [saveError, setSaveError] = useState<string | null>(null);

	const [formData, setFormData] = useState({
		productId: undefined as number | undefined,
		sku: "",
		quantity: 1,
		workstation: "",
		startTime: new Date(),
		endTime: null as Date | null,
		laborStandardId: undefined as number | undefined,
		// Which step of the standard this order is. Undefined = every operation
		// of the standard that runs on the chosen work center.
		operationId: undefined as number | undefined,
		batchNumber: "",
		orderType: "production",
	});

	// Non-production blocks (cleaning, changeover, …) carry no product,
	// labor standard, quantity, or configurable attributes — just a label.
	const isNonProduction = isNonProductionOrder(formData.orderType);
	const productSearch = useProductMenuSearch(products, formData.productId);

	useEffect(() => {
		if (open) {
			// Fetch labor standards (products and workcenters come from parent)
			laborStandardsApi.getAll()
				.then((standards) => setLaborStandards(standards))
				.catch((err) => {
					console.error("Failed to fetch labor standards:", err);
					setLaborStandards([]);
				});

			// Fetch the company's configurable work-order attribute definitions
			setAttributeErrors({});
			setSaveError(null);
			if (companyId) {
				workOrderAttributesApi.getByCompany(companyId)
					.then((definitions) => setAttributeDefinitions(definitions))
					.catch((err) => {
						console.error("Failed to fetch work order attribute definitions:", err);
						setAttributeDefinitions([]);
					});
			} else {
				setAttributeDefinitions([]);
			}
			setAttributeValues(parseAttributeValues(editingMO?.attributeValues));

			if (editingMO) {
				setFormData({
					productId: editingMO.productId,
					sku: editingMO.sku || "",
					quantity: editingMO.quantity,
					workstation: editingMO.workstation,
					startTime: new Date(editingMO.startTime),
					endTime: editingMO.endTime ? new Date(editingMO.endTime) : null,
					laborStandardId: editingMO.laborStandardId,
					operationId: editingMO.operationId ?? undefined,
					batchNumber: editingMO.batchNumber || "",
					orderType: isNonProductionOrder(editingMO.orderType)
						? editingMO.orderType
						: "production",
				});
			} else {
				// Pre-fill workcenter and date from parent when adding a new MO
				const startDate = preselectedDate
					? new Date(preselectedDate + "T08:00:00")
					: new Date();
				setFormData({
					productId: undefined,
					sku: "",
					quantity: 1,
					workstation: preselectedWorkcenter || "",
					startTime: startDate,
					endTime: null,
					laborStandardId: undefined,
					operationId: undefined,
					batchNumber: "",
					orderType: "production",
				});
			}
		}
	}, [open, editingMO, preselectedWorkcenter, preselectedDate, companyId]);

	// Orders made on the board or by upload carry their batch label as the
	// card label (sku) but not in a required "Batch #"-style field. Fill such
	// an empty field from the label so the order can be edited at all.
	useEffect(() => {
		if (!open || !editingMO?.sku || attributeDefinitions.length === 0) {
			return;
		}
		setAttributeValues((prev) => {
			const next = { ...prev };
			let changed = false;
			attributeDefinitions.forEach((definition) => {
				const key = `${definition.attrKey} ${definition.label}`.toLowerCase();
				if (definition.isRequired && key.includes("batch") && !(next[definition.attrKey] || "").trim()) {
					next[definition.attrKey] = editingMO.sku as string;
					changed = true;
				}
			});
			return changed ? next : prev;
		});
	}, [open, editingMO, attributeDefinitions]);

	// Standards with a STEP on the chosen work center — the work center lives on
	// each operation now, so a 3-step recipe is offered on all three lines.
	const standardsForWorkcenter = useMemo(
		() => laborStandards.filter((ls) => standardHasStepOn(ls, formData.workstation)),
		[laborStandards, formData.workstation]
	);

	const selectedStandard = useMemo(
		() => laborStandards.find((ls) => ls.id === formData.laborStandardId),
		[laborStandards, formData.laborStandardId]
	);

	// The chosen standard's operations that run on the chosen work center. One
	// can be pinned as THE step; left unpinned, the order is every one of them.
	const operationsOnWorkcenter = useMemo(
		() =>
			[...(selectedStandard?.operations ?? [])]
				.filter((op) => operationRunsOn(op, formData.workstation))
				.sort((a, b) => a.sequence - b.sequence),
		[selectedStandard, formData.workstation]
	);

	const standardLabel = (ls: LaborStandard) =>
		`${ls.sku || ls.productName || "Standard"}${ls.recipeGroup ? ` · ${ls.recipeGroup}` : ""} (${ls.batchSize} ${ls.batchUnit || "units"})`;

	const handleAttributeChange = useCallback((definition: WorkOrderAttributeDefinition, value: string) => {
		setAttributeValues((prev) => ({ ...prev, [definition.attrKey]: value }));
		setAttributeErrors((prev) => {
			const error = validateAttributeValue(definition, value);
			const next = { ...prev };
			if (error) {
				next[definition.attrKey] = error;
			} else {
				delete next[definition.attrKey];
			}
			return next;
		});
	}, []);

	const handleSubmit = async () => {
		// Client-side validation of configurable attributes (required/regex/type).
		// Non-production blocks skip attributes entirely — they are not product orders.
		if (!isNonProduction && attributeDefinitions.length > 0) {
			const errors: Record<string, string> = {};
			attributeDefinitions.forEach((definition) => {
				const error = validateAttributeValue(definition, attributeValues[definition.attrKey]);
				if (error) errors[definition.attrKey] = error;
			});
			setAttributeErrors(errors);
			if (Object.keys(errors).length > 0) {
				setSaveError(`Fill in: ${Object.values(errors).join(", ")}`);
				return;
			}
		}

		setLoading(true);
		setSaveError(null);
		try {
			const cleanedValues: Record<string, string> = {};
			if (!isNonProduction) {
				attributeDefinitions.forEach((definition) => {
					const value = (attributeValues[definition.attrKey] || "").trim();
					if (value) cleanedValues[definition.attrKey] = value;
				});
			}

			const payload = {
				productId: isNonProduction ? undefined : formData.productId,
				sku: formData.sku || undefined,
				quantity: isNonProduction ? 1 : formData.quantity,
				workstation: formData.workstation,
				// Plant-local wall time, as the rest of the planner sends it
				// (toISOString would shift the order by the UTC offset).
				startTime: toLocalIso(formData.startTime),
				endTime: formData.endTime ? toLocalIso(formData.endTime) : undefined,
				laborStandardId: isNonProduction ? undefined : formData.laborStandardId,
				operationId: isNonProduction ? undefined : formData.operationId ?? null,
				batchNumber: formData.batchNumber.trim() || undefined,
				locationId: selectedLocationId ?? editingMO?.locationId ?? undefined,
				orderType: formData.orderType,
				attributeValues:
					Object.keys(cleanedValues).length > 0 ? JSON.stringify(cleanedValues) : undefined,
			};

			if (editingMO) {
				await productionPlanApi.update(editingMO.id, payload);
			} else {
				await productionPlanApi.create(payload);
			}
			onSave();
		} catch (error: any) {
			console.error("Failed to save MO:", error);
			setSaveError(error?.response?.data?.message || error?.message || "The job order could not be saved.");
		} finally {
			setLoading(false);
		}
	};

	return (
		<Dialog
			open={open}
			onClose={onClose}
			maxWidth="sm"
			fullWidth
			PaperProps={{
				sx: { borderRadius: `${planner.radius}px`, boxShadow: planner.shadowDialog },
			}}
		>
			<DialogTitle sx={{ ...planner.type.title, color: planner.navy }}>
				{editingMO ? "Edit job order" : "Add job order"}
				<IconButton
					onClick={onClose}
					sx={{ position: "absolute", right: 8, top: 8, color: planner.inkMuted }}
				>
					<CloseIcon />
				</IconButton>
			</DialogTitle>
			<DialogContent dividers sx={formControlsSx}>
				{saveError && (
					<Alert severity="error" onClose={() => setSaveError(null)} sx={{ mb: 1 }}>
						{saveError}
					</Alert>
				)}
				<Box sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>
					<FormControl fullWidth>
						<InputLabel>Order type</InputLabel>
						<Select
							value={formData.orderType}
							label="Order type"
							onChange={(e) =>
								setFormData((prev) => ({
									...prev,
									orderType: e.target.value as string,
								}))
							}
						>
							{ORDER_TYPE_OPTIONS.map((option) => (
								<MenuItem key={option.value} value={option.value}>
									{option.label}
								</MenuItem>
							))}
						</Select>
					</FormControl>

					{isNonProduction && (
						<TextField
							label="Label"
							value={formData.sku}
							onChange={(e) =>
								setFormData((prev) => ({ ...prev, sku: e.target.value }))
							}
							placeholder='e.g. "Type-A clean"'
							helperText="Shown on the block in the plan"
							fullWidth
						/>
					)}

					{!isNonProduction && attributeDefinitions.map((definition) => {
						const value = attributeValues[definition.attrKey] || "";
						const error = attributeErrors[definition.attrKey];
						const label = definition.isRequired ? `${definition.label} *` : definition.label;
						if (definition.dataType === "enum") {
							const options = parseEnumOptions(definition.enumOptions);
							return (
								<FormControl fullWidth key={definition.attrKey} error={Boolean(error)}>
									<InputLabel>{label}</InputLabel>
									<Select
										value={value}
										label={label}
										onChange={(e) => handleAttributeChange(definition, e.target.value as string)}
									>
										{!definition.isRequired && (
											<MenuItem value="">
												<em>None</em>
											</MenuItem>
										)}
										{options.map((option) => (
											<MenuItem key={option} value={option}>
												{option}
											</MenuItem>
										))}
									</Select>
									{error && <FormHelperText>{error}</FormHelperText>}
								</FormControl>
							);
						}
						return (
							<TextField
								key={definition.attrKey}
								label={label}
								type={
									definition.dataType === "number"
										? "number"
										: definition.dataType === "date"
											? "date"
											: "text"
								}
								value={value}
								onChange={(e) => handleAttributeChange(definition, e.target.value)}
								error={Boolean(error)}
								helperText={error}
								fullWidth
								InputLabelProps={definition.dataType === "date" ? { shrink: true } : undefined}
							/>
						);
					})}

					{!isNonProduction && (
						<FormControl fullWidth>
							<InputLabel>Product</InputLabel>
							<Select
								value={formData.productId || ""}
								label="Product"
								{...productSearch.selectProps}
								onChange={(e) =>
									setFormData((prev) => ({
										...prev,
										productId: e.target.value as number,
									}))
								}
							>
								<ProductMenuSearch query={productSearch.query} onQueryChange={productSearch.setQuery} />
								<MenuItem value="">
									<em>None (use SKU)</em>
								</MenuItem>
								{productSearch.visible.map((product) => (
									<MenuItem key={product.id} value={product.id}>
										{product.name} ({product.itemNumber})
									</MenuItem>
								))}
								{productSearch.isEmpty && <ProductMenuEmpty />}
							</Select>
						</FormControl>
					)}

					{!isNonProduction && !formData.productId && (
						<TextField
							label="SKU"
							value={formData.sku}
							onChange={(e) =>
								setFormData((prev) => ({ ...prev, sku: e.target.value }))
							}
							fullWidth
						/>
					)}

					{!isNonProduction && (
						<TextField
							label="Quantity"
							type="number"
							value={formData.quantity}
							onChange={(e) =>
								setFormData((prev) => ({
									...prev,
									quantity: parseInt(e.target.value) || 1,
								}))
							}
							fullWidth
							inputProps={{ min: 1 }}
						/>
					)}

				<FormControl fullWidth>
					<InputLabel>Workstation</InputLabel>
					<Select
						value={formData.workstation}
						label="Workstation"
						onChange={(e) =>
							setFormData((prev) => ({
								...prev,
								workstation: e.target.value,
								// A pinned operation belongs to a work center; re-pick it
								operationId: undefined,
							}))
						}
					>
						{workcenters.map((wc) => (
							<MenuItem key={wc} value={wc}>
								{wc}
							</MenuItem>
						))}
					</Select>
				</FormControl>

					<LocalizationProvider dateAdapter={AdapterDateFns}>
						<DateTimePicker
							label="Start Time"
							value={formData.startTime}
							onChange={(newValue) =>
								setFormData((prev) => ({
									...prev,
									startTime: newValue || new Date(),
								}))
							}
						/>
						<DateTimePicker
							label="End Time (Optional)"
							value={formData.endTime}
							onChange={(newValue) =>
								setFormData((prev) => ({ ...prev, endTime: newValue }))
							}
						/>
					</LocalizationProvider>

					{!isNonProduction && (
						<TextField
							label="Batch / order number"
							value={formData.batchNumber}
							onChange={(e) =>
								setFormData((prev) => ({ ...prev, batchNumber: e.target.value }))
							}
							placeholder="Process order, batch or job order number"
							fullWidth
						/>
					)}

					{!isNonProduction && (
						<FormControl fullWidth>
							<InputLabel>Labor Standard</InputLabel>
							<Select
								value={formData.laborStandardId || ""}
								label="Labor Standard"
								onChange={(e) =>
									setFormData((prev) => ({
										...prev,
										laborStandardId: (e.target.value as number | "") || undefined,
										operationId: undefined,
									}))
								}
							>
								<MenuItem value="">
									<em>None</em>
								</MenuItem>
								{standardsForWorkcenter.map((ls) => (
									<MenuItem key={ls.id} value={ls.id}>
										{standardLabel(ls)}
									</MenuItem>
								))}
							</Select>
							{formData.workstation && standardsForWorkcenter.length === 0 && (
								<FormHelperText>No labor standard has a step on {formData.workstation}</FormHelperText>
							)}
						</FormControl>
					)}

					{!isNonProduction && selectedStandard && operationsOnWorkcenter.length > 0 && (
						<FormControl fullWidth>
							<InputLabel>Operation (step)</InputLabel>
							<Select
								value={formData.operationId ?? ""}
								label="Operation (step)"
								onChange={(e) =>
									setFormData((prev) => ({
										...prev,
										operationId: (e.target.value as number | "") || undefined,
									}))
								}
							>
								<MenuItem value="">
									<em>
										{operationsOnWorkcenter.length > 1
											? `All ${operationsOnWorkcenter.length} steps on ${formData.workstation}`
											: "Let the planner pick"}
									</em>
								</MenuItem>
								{operationsOnWorkcenter.map((op) => (
									<MenuItem key={op.id} value={op.id}>
										{op.sequence}. {op.name || op.operationTypeName || op.operationType?.name || "Operation"}
										{` · ${Math.round((op.durationMinutes || 0) / 6) / 10}h`}
									</MenuItem>
								))}
							</Select>
							<FormHelperText>
								{operationsOnWorkcenter.length > 1
									? "This order is one step of the recipe — pin the operation it runs, or leave it to run every step on this work center"
									: "The only step of this standard on the chosen work center"}
							</FormHelperText>
						</FormControl>
					)}
				</Box>
			</DialogContent>
			<DialogActions sx={{ px: 3, py: 2 }}>
				<GhostButton size="small" onClick={onClose}>Cancel</GhostButton>
				<PrimaryButton
					size="small"
					onClick={handleSubmit}
					disabled={
						loading ||
						!formData.workstation
					}
				>
					{loading ? "Saving..." : editingMO ? "Update" : "Create"}
				</PrimaryButton>
			</DialogActions>
		</Dialog>
	);
};

export default MOForm;
